import type { AutonomyLevel } from '@contracts/assistant';
import { ORDER_EVENT_WHEN, orderBranchWhen, type OrderDTO, type OrderOffReason } from '@contracts/assistant-orders';
import type {
  MissionCardStep, MissionListItemDTO, MissionPauseReason, MissionStatus, MissionStepDTO, RunLogResult, RunLogRowDTO,
  RunStatusKind, RunTriggerKind,
} from '@contracts/assistant-missions';
import { BUSINESS_TIME_ZONE, businessDate, daysBetween } from '@domain/dates';
import { describeRecurrence } from '@domain/recurrence';
import { LEVEL_LABEL } from '../autonomy/labels';

/**
 * The mission plan card's words (S7 §6.2, P18/D128, mockup §1). Strings the mockup or P18 does not write are marked
 * "invented" below, so a copy pass finds them.
 */

const count = (n: number): string => n.toLocaleString('vi-VN');

/** What Hana does by herself at each level, between the level's name and the run limits (P18). */
const HOW: Record<AutonomyLevel, (persona: string) => string> = {
  ask: (persona) => `mọi thao tác ${persona} đều hỏi bạn trước.`,
  light: (persona) => `${persona} tự ghi chú và đánh dấu xong trên thẻ của bạn; việc khác hỏi bạn.`,
  // Mockup :225 verbatim.
  bounded: (persona) => `sau khi đọc thư, ${persona} vẫn tự ghi chú, đánh dấu xong trên thẻ của bạn. Thư gửi ra ngoài và `
    + 'việc chạm tới người khác luôn hỏi.',
};

/** "<persona> sẽ làm thế nào": the level's name (bold on the card), then what it allows, then the run limits. */
export function howSentence(level: AutonomyLevel, persona: string, runsPerDay: number, runTokenCap: number):
  { lead: string; level: string; rest: string } {
  return {
    lead: 'Theo mức tự chủ ',
    level: LEVEL_LABEL[level],
    rest: ` của bạn: ${HOW[level](persona)} Tối đa ${count(runsPerDay)} lượt mỗi ngày, mỗi lượt ${count(runTokenCap)} token, `
      + 'trừ vào hạn mức của bạn.',
  };
}

export const MISSION_CARD = {
  heading: 'Kế hoạch nhiệm vụ',
  draft: 'Nháp',
  brief: 'Lời giao của bạn',
  verbatim: 'nguyên văn',
  place: 'Đặt dưới',
  newRoot: 'Thẻ gốc mới',
  steps: 'Các bước',
  how: (persona: string) => `${persona} sẽ làm thế nào`,
  approve: 'Duyệt kế hoạch',
  edit: 'Sửa',
  cancel: 'Hủy',
  hint: 'Bỏ chọn một bước để không tạo',
  other: 'Người khác',
  /** Invented: the checkbox's and the rename field's accessible names. */
  pick: (title: string) => `Chọn bước: ${title}`,
  stepName: 'Tên bước',
  goalName: 'Tên mục tiêu',
  /** Mockup :225: the muted word after the "Đặt dưới" chip that opens the picker. */
  change: '· đổi',
  /** Invented: the picker trigger's accessible name, and the line while the board is read (D155). */
  changePlace: 'Đổi chỗ đặt mục tiêu',
  loadingBoard: 'Đang tải bản đồ…',
} as const;

/**
 * D205: the `order` card's copy the server builds (the event "Khi nào" words, the level lines, ★ P21) lives in
 * contracts; the web reads it from there, never a copy of its own.
 */
export { ORDER_EVENT_WHEN, ORDER_LEVEL_LINE, orderBranchWhen } from '@contracts/assistant-orders';

/** The standing-order confirm card's words (S7 §7.1, P7; mockup §3 left :330-340). "Invented" marks the rest. */
export const ORDER_CARD = {
  heading: 'Tạo lệnh thường trực',
  /** C-F1 (D222): the same card for a change to an order (`order.update`), the order as it will be. */
  updateHeading: 'Sửa lệnh thường trực',
  name: 'Tên',
  instruction: 'Lời dặn',
  verbatim: 'nguyên văn',
  when: 'Khi nào',
  uses: 'Dùng',
  level: 'Mức tự chủ',
  confirm: 'Xác nhận',
  edit: 'Sửa',
  reject: 'Từ chối',
  save: 'Lưu',
  cancel: 'Hủy',
  /** Invented: the project chip's kind word (mockup :337 "Bán hàng · dự án"). */
  project: (name: string) => `${name} · dự án`,
  /** Invented: "Dùng" when the order names nothing it expects to use. */
  noUses: 'Không chỉ định',
  /** Invented: under the Sửa form, what the card does not change (P7: name and instruction only). */
  editNote: (persona: string) => `Chỉ sửa tên và lời dặn ở đây. Muốn đổi lúc chạy hay mức tự chủ, nhắn ${persona}.`,
} as const;

const ddMM = (date: string): string => `${date.slice(8, 10)}/${date.slice(5, 7)}`;

/**
 * Who does a step, and by when (mockup :212-216): "Hana làm · 07/10", "Giao cho An · 14/10". Invented: "Bạn làm" for
 * the owner's own step (the mockup has none).
 */
export function doerLine(step: Pick<MissionCardStep, 'executor' | 'assignee' | 'deadline'>, persona: string,
  given: (name: string) => string): string {
  const who = step.executor === 'hana' ? `${persona} làm`
    : step.executor === 'owner' ? 'Bạn làm'
      : `Giao cho ${step.assignee === null ? MISSION_CARD.other : given(step.assignee.name)}`;
  return step.deadline === null ? who : `${who} · ${ddMM(step.deadline)}`;
}

/**
 * What a step waits for (P18): "· sau N bước trên" when it names every step above it, else the 1-based places — a run
 * as "· sau bước 1–3", one as "· sau bước 2", others joined with ", " (invented). Refs that are not steps are ignored.
 */
export function afterText(after: readonly string[], index: number, refs: readonly string[]): string | null {
  const places = [...new Set(after.map((r) => refs.indexOf(r)).filter((i) => i >= 0 && i !== index))].sort((a, b) => a - b);
  if (places.length === 0) return null;
  if (index > 0 && places.length === index && places.every((p, i) => p === i)) return `· sau ${index} bước trên`;
  return placesText(places.map((p) => p + 1));
}

/** Sorted 1-based places as "· sau bước 1–3", "· sau bước 2", "· sau bước 1, 3–4" (the page's steps, mockup §2). */
export function placesText(places: readonly number[]): string | null {
  if (places.length === 0) return null;
  const runs: string[] = [];
  let start = places[0]!;
  let end = start;
  for (const p of [...places.slice(1), Number.NaN]) {
    if (p === end + 1) { end = p; continue; }
    runs.push(start === end ? `${start}` : `${start}–${end}`);
    start = p;
    end = p;
  }
  return `· sau bước ${runs.join(', ')}`;
}

/** The undo refused as stale (G3, `hint: 'cancel_mission'`): the offer, and what its press says. Invented: the latter. */
export const CANCEL_MISSION = {
  action: 'Hủy nhiệm vụ',
  done: 'Đã hủy nhiệm vụ',
} as const;

/**
 * The `missionDiff` card (S7 §6.6, D131): the S5 batch card's pills, and each change as "before → after". Invented (no
 * mockup draws the card): the field names, "không có", "Duyệt"/"Từ chối" kept from the confirm card.
 */
export const DIFF_CARD = {
  heading: 'Đổi kế hoạch',
  pill: { add: 'Mới', update: 'Đổi', remove: 'Bỏ' },
  approve: 'Duyệt',
  reject: 'Từ chối',
  under: (title: string) => `dưới ${title}`,
} as const;

const NONE = 'không có';

type DiffWho = { executor?: MissionCardStep['executor']; assignee?: MissionCardStep['assignee'] };

/** Who does a step, in a diff row: "Hana", "Bạn", the colleague's given name. */
function whoText(f: DiffWho, persona: string, given: (name: string) => string): string {
  if (f.executor === 'hana') return persona;
  if (f.executor === 'owner') return 'Bạn';
  if (f.assignee !== undefined && f.assignee !== null) return given(f.assignee.name);
  return f.executor === 'other' ? MISSION_CARD.other : NONE;
}

/** One changed field of an `update` row, before → after (D131 shows only the changed fields). */
export function diffChanges(before: DiffWho & { title?: string; deadline?: string | null; after?: string[] },
  after: DiffWho & { title?: string; deadline?: string | null; after?: string[] }, persona: string,
  given: (name: string) => string): { key: 'title' | 'deadline' | 'executor' | 'after'; text: string }[] {
  const out: { key: 'title' | 'deadline' | 'executor' | 'after'; text: string }[] = [];
  if ('title' in after) out.push({ key: 'title', text: `Tên ${before.title ?? NONE} → ${after.title ?? NONE}` });
  if ('deadline' in after) {
    const d = (v: string | null | undefined) => (v === null || v === undefined ? NONE : ddMM(v));
    out.push({ key: 'deadline', text: `Hạn ${d(before.deadline)} → ${d(after.deadline)}` });
  }
  if ('executor' in after || 'assignee' in after) {
    // An assignee change alone keeps the executor it had.
    const was = { executor: before.executor, assignee: before.assignee };
    const now = { executor: after.executor ?? before.executor, assignee: after.assignee ?? before.assignee };
    out.push({ key: 'executor', text: `Người làm ${whoText(was, persona, given)} → ${whoText(now, persona, given)}` });
  }
  if ('after' in after) {
    const n = (v: string[] | undefined) => (v === undefined || v.length === 0 ? 'không chờ' : `chờ ${v.length} bước`);
    out.push({ key: 'after', text: `Thứ tự ${n(before.after)} → ${n(after.after)}` });
  }
  return out;
}

/**
 * D136: the run card of a mission paused because its goal card was deleted (tray and page), and the chip to the
 * missions page. Both from the ledger's ruling.
 */
export const STALE_RUN = {
  text: (persona: string) => `${persona} tạm dừng nhiệm vụ vì thẻ mục tiêu đã bị xoá.`,
  open: 'Mở trang Nhiệm vụ',
} as const;

/**
 * The missions page (S7 §9.1, mockup §2). Invented (no mockup or ruling writes them): `empty`, `emptyHint`, `back`,
 * `pending`, `noSteps`, `noRuns`, `notFound`.
 */
export const MISSIONS_PAGE = {
  /** The reader's own persona name (S8 D9). */
  title: (persona: string) => `Nhiệm vụ của ${persona}`,
  tabs: { missions: 'Nhiệm vụ', orders: 'Lệnh thường trực', runs: 'Nhật ký chạy' },
  tabsLabel: (persona: string) => `Các phần của Nhiệm vụ của ${persona}`,
  // P15 / §11.
  adminOff: 'Admin đang tắt nhiệm vụ và lệnh thường trực.',
  empty: 'Chưa có nhiệm vụ nào',
  emptyHint: (persona: string) =>
    `Giao một việc lớn cho ${persona} trong khung chat: ${persona} lập kế hoạch, bạn duyệt rồi ${persona} làm dần.`,
  listLabel: 'Danh sách nhiệm vụ',
  back: 'Danh sách nhiệm vụ',
  openOnMap: 'Mở trên Workplace',
  steps: 'Các bước',
  pending: 'Chờ xác nhận',
  journal: 'Nhật ký',
  all: (n: number) => `Xem cả ${n} lượt`,
  noSteps: 'Nhiệm vụ chưa có bước nào.',
  noRuns: 'Chưa có lượt chạy nào.',
  notFound: 'Không tìm thấy nhiệm vụ này.',
  done: 'Xong',
  cancelled: 'Đã hủy',
  /** The list row's count: the questions and the cards waiting on the reader. */
  waiting: (n: number) => `${n} việc chờ bạn`,
} as const;

const CLOCK = new Intl.DateTimeFormat('vi-VN', { timeZone: BUSINESS_TIME_ZONE, hour: 'numeric', minute: '2-digit' });
const TOKENS = new Intl.NumberFormat('vi-VN');
/** A timestamp's business day as "dd/MM". */
const dayOf = (iso: string): string => ddMM(businessDate(new Date(iso)));
const daysFrom = (iso: string, now: Date): number => daysBetween(businessDate(now), businessDate(new Date(iso)));

/** Mockup §2 for `quota`; D136 for `stale`. Invented: owner, budget, admin, access, error. */
const PAUSE_REASON: Record<MissionPauseReason, string> = {
  owner: 'bạn tạm dừng',
  quota: 'hết hạn mức hôm nay',
  budget: 'hết ngân sách token',
  admin: 'admin tắt nhiệm vụ',
  access: 'tài khoản không dùng trợ lý được',
  stale: 'thẻ mục tiêu đã bị xoá',
  error: 'lỗi ba lượt liền',
};
/** Mockup §2: "Đang chạy", "Chờ bạn", "Tạm dừng", "Xong". Invented: "Đã hủy". */
const STATUS: Record<MissionStatus, string> = {
  active: 'Đang chạy', waiting: 'Chờ bạn', paused: 'Tạm dừng', done: 'Xong', cancelled: 'Đã hủy',
};

/** When the next run is (mockup §2 "lượt kế 8:00 mai"): today the clock alone, tomorrow "mai", later "dd/MM". */
export function nextRunText(iso: string, now: Date = new Date()): string {
  const days = daysFrom(iso, now);
  const clock = CLOCK.format(new Date(iso));
  if (days <= 0) return clock;
  if (days === 1) return `${clock} mai`;
  return `${clock} ${dayOf(iso)}`;
}

/**
 * A list row's line (mockup §2): "Chờ bạn · 1 câu hỏi · hạn 31/10", "Đang chạy · lượt kế 8:00 mai", "Tạm dừng · hết hạn
 * mức hôm nay", "Xong · 28/09". Invented: "N chờ xác nhận" on a waiting row, "Đã hủy · dd/MM".
 */
export function missionMeta(m: MissionListItemDTO, now: Date = new Date()): string {
  const parts: string[] = [STATUS[m.status]];
  if (m.status === 'done' || m.status === 'cancelled') {
    if (m.finishedAt !== null) parts.push(dayOf(m.finishedAt));
    return parts.join(' · ');
  }
  if (m.status === 'waiting') {
    if (m.hasAsk) parts.push('1 câu hỏi');
    if (m.pendingCount > 0) parts.push(`${m.pendingCount} chờ xác nhận`);
  }
  if (m.status === 'paused' && m.pauseReason !== null) parts.push(PAUSE_REASON[m.pauseReason]);
  if (m.status === 'active' && m.nextRunAt !== null) parts.push(`lượt kế ${nextRunText(m.nextRunAt, now)}`);
  if (m.deadline !== null) parts.push(`hạn ${ddMM(m.deadline)}`);
  return parts.join(' · ');
}

/** The detail's header line (mockup §2): "2/4 bước · hạn 31/10". */
export const progressText = (m: Pick<MissionListItemDTO, 'done' | 'total' | 'deadline'>): string =>
  m.deadline === null ? `${m.done}/${m.total} bước` : `${m.done}/${m.total} bước · hạn ${ddMM(m.deadline)}`;

/** How a step's mark looks (mockup §2 `.ic`): done ✓, Hana under way, someone else under way "!", not started. */
export type StepMark = 'done' | 'run' | 'wait' | 'todo';
export const stepMark = (s: Pick<MissionStepDTO, 'status' | 'executor'>): StepMark => {
  if (s.status === 'completed') return 'done';
  if (s.status === 'in_progress') return s.executor === 'hana' ? 'run' : 'wait';
  return 'todo';
};

/**
 * A step's line (mockup §2): "Xong 05/10", "An đang làm · hạn 14/10 · Hana chỉ theo dõi", "Chưa bắt đầu · sau bước 1–3".
 * `after` is the step's 1-based places. Invented: "Bạn đang làm", "<persona> đang làm", "· hạn" on a step not started.
 */
export function stepMeta(s: MissionStepDTO, index: number, persona: string, given: (name: string) => string): string {
  if (s.status === 'completed') return s.completedAt === null ? STATUS.done : `${STATUS.done} ${dayOf(s.completedAt)}`;
  const deadline = s.deadline === null ? '' : ` · hạn ${ddMM(s.deadline)}`;
  if (s.status === 'in_progress') {
    const who = s.executor === 'hana' ? persona : s.executor === 'owner' ? 'Bạn'
      : s.assignee === null ? MISSION_CARD.other : given(s.assignee.name);
    return `${who} đang làm${deadline}${s.executor === 'hana' ? '' : ` · ${persona} chỉ theo dõi`}`;
  }
  const after = placesText([...new Set(s.after)].filter((p) => p !== index + 1).sort((a, b) => a - b));
  return `Chưa bắt đầu${after === null ? deadline : ` ${after}`}`;
}

/** A journal entry's time (mockup §2): "Hôm nay 9:12", "Hôm qua 15:30", else "05/10 10:02". */
export function journalTime(iso: string, now: Date = new Date()): string {
  const days = -daysFrom(iso, now);
  const clock = CLOCK.format(new Date(iso));
  if (days <= 0) return `Hôm nay ${clock}`;
  if (days === 1) return `Hôm qua ${clock}`;
  return `${dayOf(iso)} ${clock}`;
}

/** Mockup §2's token pill: "8.420 token". */
export const tokenText = (n: number): string => `${TOKENS.format(n)} token`;

/**
 * ★ Invented (P21/D197, for the PO copy pass): why a standing order turned itself off (P5) — the tray card's and run
 * log's line, and the bell's notice "<what> · <order name>".
 */
export const ORDER_OFF = {
  reason: {
    errors: 'lỗi 3 lần liên tiếp',
    access: 'tài khoản không dùng trợ lý được',
    branch: 'nhánh theo dõi không còn',
    ended: 'lịch đã hết',
  } satisfies Record<OrderOffReason, string>,
  line: (reason: OrderOffReason): string => `Lệnh đã tắt vì ${ORDER_OFF.reason[reason]}.`,
  notice: (reason: OrderOffReason): string => `Lệnh thường trực đã tắt vì ${ORDER_OFF.reason[reason]}`,
  /** ★ invented (whole-S7 review C2, marked per D156): the off run card's link to its order, beside STALE_RUN.open. */
  open: 'Mở lệnh',
};

/**
 * What a run came to, in words (P16, D126): the mockup's "Chờ bạn · 1 câu hỏi", "Hết hạn mức hôm nay · tạm dừng"; P16's
 * "N chờ xác nhận", "N thẻ tạo". Invented: "Đang chạy…", "Đã dừng", "Xong"; ★ invented (D163, marked per D156): "N thay
 * đổi" — a run that changed cards but made none. A failure is the §11 text of its code.
 */
export function runResultText(r: RunLogResult, errorText: (code: string) => string): string {
  switch (r.kind) {
    case 'ask': return 'Chờ bạn · 1 câu hỏi';
    case 'pending': return `${TOKENS.format(r.count)} chờ xác nhận`;
    case 'created': return `${TOKENS.format(r.count)} thẻ tạo`;
    case 'changed': return `${TOKENS.format(r.count)} thay đổi`;
    case 'quota': return 'Hết hạn mức hôm nay · tạm dừng';
    case 'failed': return errorText(r.code);
    case 'live': return 'Đang chạy…';
    case 'cancelled': return 'Đã dừng';
    case 'off': return ORDER_OFF.line(r.reason);
    case 'summary': return 'Xong';
  }
}

/**
 * The ask block (S7 §9.1, mockup §2 `.ask`): "<persona> hỏi bạn", the field's "Trả lời <persona>…", "Gửi". Invented:
 * the limit line, the line of a paused mission's question, the sent toast.
 */
export const ASK_BLOCK = {
  heading: (persona: string) => `${persona} hỏi bạn`,
  field: (persona: string) => `Trả lời ${persona}`,
  placeholder: (persona: string) => `Trả lời ${persona}…`,
  send: 'Gửi',
  options: 'Gợi ý trả lời',
  tooLong: 'Câu trả lời tối đa 2.000 ký tự.',
  paused: 'Tiếp tục nhiệm vụ để trả lời.',
  sent: (persona: string) => `Đã gửi câu trả lời cho ${persona}`,
} as const;

/** When Hana asked (mockup §2 "9:12"): today the clock alone, else the journal's words. */
export const askedTime = (iso: string, now: Date = new Date()): string =>
  (daysFrom(iso, now) === 0 ? CLOCK.format(new Date(iso)) : journalTime(iso, now));

/**
 * The detail's header controls (S7 §9.1: "chọn mức, Tạm dừng/Tiếp tục, menu Hủy/Chạy ngay/Sửa trần"; mockup §2; P19
 * "Xoá"). Invented: the menu's and the picker's accessible names, the confirm dialogs' words, the toasts.
 */
export const MISSION_ACTIONS = {
  level: (label: string) => `Mức của nhiệm vụ: ${label}`,
  pause: 'Tạm dừng',
  resume: 'Tiếp tục',
  more: 'Thêm thao tác',
  runNow: 'Chạy ngay',
  limits: 'Sửa trần',
  cancel: CANCEL_MISSION.action,
  remove: 'Xoá nhiệm vụ',
  paused: 'Đã tạm dừng nhiệm vụ',
  resumed: 'Đã tiếp tục nhiệm vụ',
  cancelled: CANCEL_MISSION.done,
  removed: 'Đã xoá nhiệm vụ',
  started: (persona: string) => `${persona} bắt đầu một lượt`,
  levelSaved: (label: string) => `Mức của nhiệm vụ: ${label}`,
  cancelTitle: 'Hủy nhiệm vụ này?',
  cancelBody: (persona: string) => `${persona} dừng hẳn nhiệm vụ và không chạy thêm lượt nào. Các thẻ đã tạo vẫn giữ nguyên.`,
  keep: 'Giữ lại',
  removeTitle: 'Xoá nhiệm vụ này?',
  removeBody: 'Nhiệm vụ và nhật ký chạy của nó sẽ bị xoá. Các thẻ trên Workplace vẫn giữ nguyên.',
  removeAction: 'Xoá',
  back: 'Hủy',
} as const;

/** "Sửa trần" (S7 §6.6, §9.2; 027's bounds). Invented: every word. */
export const LIMITS_DIALOG = {
  title: 'Sửa trần',
  runsPerDay: 'Số lượt mỗi ngày',
  runTokenCap: 'Token tối đa mỗi lượt',
  tokenBudget: 'Ngân sách token cả nhiệm vụ',
  used: (n: number) => `Đã dùng ${TOKENS.format(n)} token.`,
  range: (min: number, max: number, unit: string) => `Từ ${TOKENS.format(min)} đến ${TOKENS.format(max)} ${unit}.`,
  save: 'Lưu',
  cancel: 'Hủy',
  saved: 'Đã lưu trần của nhiệm vụ',
} as const;

/** P16 (D126): how a run started, under its mission's name. */
export const RUN_TRIGGER: Record<RunTriggerKind, string> = {
  start: 'lượt đầu', schedule: 'lần kiểm theo lịch', event: 'theo sự kiện', answer: 'sau câu trả lời',
  manual: 'chạy ngay', resume: 'tiếp tục',
};

/**
 * "Nhật ký chạy" (S7 §9.1, mockup §2): the two filters' first options and the table's head are the mockup's.
 * Invented: the filters' accessible names, the status words, the table's caption.
 */
export const RUN_LOG = {
  allSubjects: 'Tất cả nhiệm vụ và lệnh',
  allStatuses: 'Mọi trạng thái',
  subjectFilter: 'Lọc theo nhiệm vụ hoặc lệnh',
  statusFilter: 'Lọc theo trạng thái',
  caption: 'Mọi lượt chạy của bạn, mới nhất trước',
  columns: { at: 'Lúc', subject: 'Nhiệm vụ / lệnh', result: 'Kết quả', tokens: 'Token' },
  more: 'Xem thêm',
  status: {
    queued: 'Đang chờ chạy', running: 'Đang chạy', waiting: 'Chờ bạn', done: 'Xong', failed: 'Lỗi', cancelled: 'Đã dừng',
  } satisfies Record<RunStatusKind, string>,
} as const;

/** A result's pill tone (mockup §2): `.pill.upd` warn, `.pill.ok`, `.pill.dup` late, `.pill.mut`; Hana's for a live run. */
export type RunTone = 'warn' | 'ok' | 'late' | 'muted' | 'hana';

/**
 * A run log row's result in words and tone (P16, D126): the journal's words, with the summary itself for a finished
 * run and "· Hoàn tác còn …" while the undo is open (mockup §2 "2 thẻ tạo · Hoàn tác còn 23 giờ"; D163 "N thay đổi · …").
 */
export function runLogResult(r: Pick<RunLogRowDTO, 'result' | 'summary'>, errorText: (code: string) => string,
  left: (until: string) => string | null): { text: string; tone: RunTone } {
  const result = r.result;
  switch (result.kind) {
    case 'ask': case 'pending': return { text: runResultText(result, errorText), tone: 'warn' };
    case 'created': case 'changed': {
      const undo = left(result.undoUntil);
      const text = runResultText(result, errorText);
      return { text: undo === null ? text : `${text} · Hoàn tác ${undo}`, tone: 'ok' };
    }
    case 'quota': case 'failed': return { text: runResultText(result, errorText), tone: 'late' };
    case 'live': return { text: runResultText(result, errorText), tone: 'hana' };
    case 'cancelled': case 'off': return { text: runResultText(result, errorText), tone: 'muted' };
    case 'summary': return { text: r.summary ?? runResultText(result, errorText), tone: 'muted' };
  }
}

/**
 * "Lệnh thường trực" (S7 §9.1, mockup §3 right :343-356; L4 T13). The tab, the column heads, "+ Lệnh mới", "Chạy thử
 * ngay" and "N lệnh" are the mockup's; ★ invented (D197, for the PO copy pass): the rest — the empty state, the switch's
 * and the menu's accessible names, the confirm dialog, the toasts, "Chưa chạy lần nào", the gone branch's words.
 */
export const ORDERS_PAGE = {
  count: (n: number) => `${TOKENS.format(n)} lệnh`,
  newOrder: '+ Lệnh mới',
  caption: 'Các lệnh thường trực của bạn',
  columns: { order: 'Lệnh', when: 'Khi nào', last: 'Lần gần nhất', active: 'Bật', actions: 'Thao tác' },
  next: (when: string) => `kế: ${when}`,
  never: 'Chưa chạy lần nào',
  toggle: (name: string) => `Bật lệnh ${name}`,
  runNow: 'Chạy thử ngay',
  more: (name: string) => `Thêm thao tác: ${name}`,
  edit: 'Sửa',
  remove: 'Xoá',
  enabled: 'Đã bật lệnh',
  disabled: 'Đã tắt lệnh',
  removeTitle: 'Xoá lệnh này?',
  removeBody: (persona: string) => `${persona} sẽ không chạy lệnh này nữa; nhật ký chạy của nó bị xoá theo. `
    + 'Những gì đã làm trên Workplace vẫn giữ nguyên.',
  removeAction: 'Xoá',
  back: 'Hủy',
  removed: 'Đã xoá lệnh',
  empty: 'Chưa có lệnh thường trực nào',
  emptyHint: (persona: string) => `Nhắn ${persona} một việc lặp lại, như "mỗi sáng xem hộp thư và tạo thẻ cho thư hỏi báo `
    + 'giá", hoặc bấm "+ Lệnh mới".',
  /** ★ A `status_in_branch` order whose branch the owner no longer has. */
  branchGone: 'Khi trạng thái trong nhánh theo dõi đổi',
} as const;

/** "8:00" from "08:00" (mockup §3 "8:00 ngày làm việc"). */
const clockOf = (hhmm: string): string => `${Number(hhmm.slice(0, 2))}:${hhmm.slice(3, 5)}`;

/**
 * An order's "Khi nào" (mockup §3): a schedule as its time and its series in the reminders' words ("8:00 · Các ngày làm
 * việc"), an event in the order card's words (D205, from contracts).
 */
export function orderWhenText(o: Pick<OrderDTO, 'trigger' | 'recurrence' | 'anchorLocal' | 'time' | 'eventKind' | 'eventItem'>):
  string {
  if (o.trigger === 'schedule') {
    if (o.recurrence === null || o.anchorLocal === null || o.time === null) return '';
    return `${clockOf(o.time)} · ${describeRecurrence(o.anchorLocal, o.recurrence)}`;
  }
  if (o.eventKind === 'status_in_branch') {
    return o.eventItem === null ? ORDERS_PAGE.branchGone : orderBranchWhen(o.eventItem.title);
  }
  return o.eventKind === null ? '' : ORDER_EVENT_WHEN[o.eventKind];
}

/**
 * "+ Lệnh mới" / "Sửa" (S7 §7.1 "form cùng trường", L4 T14). The field names are the order card's (mockup §3 left:
 * "Tên", "Lời dặn", "Khi nào", "Mức tự chủ"); the event kinds read as the card reads them (`ORDER_EVENT_WHEN`). ★
 * invented (D197, for the PO copy pass): the dialog titles, the trigger and repeat words, the field errors, the toasts.
 */
export const ORDER_FORM = {
  createTitle: 'Tạo lệnh thường trực',
  editTitle: 'Sửa lệnh thường trực',
  name: 'Tên',
  instruction: 'Lời dặn',
  instructionHint: (persona: string) => `${persona} làm đúng lời dặn này mỗi lần lệnh chạy.`,
  trigger: 'Chạy khi',
  triggers: { schedule: 'Theo lịch', event: 'Theo sự kiện' },
  time: 'Giờ',
  freq: 'Lặp lại',
  freqs: { daily: 'Hằng ngày', weekly: 'Hằng tuần', monthly: 'Hằng tháng' },
  interval: 'Bao lâu một lần',
  every: (n: number, unit: string) => (n === 1 ? `Mỗi ${unit}` : `Mỗi ${n} ${unit}`),
  units: { daily: 'ngày', weekly: 'tuần', monthly: 'tháng' },
  days: 'Vào các thứ',
  dayShort: (isoDay: number) => (isoDay === 7 ? 'CN' : `T${isoDay + 1}`),
  /** C-F3 (WCAG 2.5.3): a day chip's accessible name starts with its visible text, then says it in full. */
  dayName: (isoDay: number) => (isoDay === 7 ? 'CN, chủ nhật' : `T${isoDay + 1}, thứ ${isoDay + 1}`),
  workdaysOnly: 'Chỉ ngày làm việc',
  startDate: 'Bắt đầu từ',
  startHint: 'Bỏ trống: hôm nay nếu giờ chạy chưa qua, không thì ngày mai.',
  until: 'Đến hết ngày',
  event: 'Sự kiện',
  eventPick: 'Chọn sự kiện',
  branchKind: 'Khi trạng thái trong một nhánh đổi',
  branch: 'Nhánh theo dõi',
  branchNone: 'Chưa chọn thẻ gốc',
  change: 'đổi',
  loadingBoard: 'Đang tải bảng…',
  boardFailed: 'Không tải được bảng. Hãy thử lại.',
  when: 'Khi nào',
  level: 'Mức tự chủ',
  levelHint: 'Không cao hơn mức tự chủ của anh.',
  runsPerDay: 'Tối đa lượt mỗi ngày',
  save: 'Lưu',
  cancel: 'Hủy',
  /** C-F2: after a stale-version refusal, read the order's newest version into the form. */
  reload: 'Tải lại',
  created: 'Đã tạo lệnh',
  saved: 'Đã lưu lệnh',
  errors: {
    name: 'Tên không được trống',
    instruction: 'Lời dặn không được trống',
    days: 'Chọn ít nhất một thứ',
    event: 'Chọn sự kiện',
    branch: 'Chọn thẻ gốc của nhánh',
    runsPerDay: (min: number, max: number) => `Từ ${min} đến ${max} lượt`,
  },
} as const;
